import { useEffect, useState } from "react";
import { X, Play } from "lucide-react";

export const MediaLightbox = ({ item, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!item) return null;

  return (
    <div
      data-testid="media-lightbox"
      className="fixed inset-0 z-[200] bg-[#050505]/95 backdrop-blur-md flex items-center justify-center p-4 md:p-12"
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close-btn"
        onClick={onClose}
        className="absolute top-6 right-6 border border-white/20 bg-[#050505]/70 hover:bg-[#FF3B30] hover:border-[#FF3B30] text-white p-3 transition-colors"
        aria-label="Close"
      >
        <X size={18} />
      </button>
      <div className="absolute top-6 left-6 font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/50">
        Media Viewer / ESC to close
      </div>
      <div
        className="relative max-w-7xl max-h-[85vh] w-full flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {item.type === "image" ? (
          <img
            src={item.url}
            alt={item.alt || "Project media"}
            className="max-h-[85vh] max-w-full object-contain border border-white/10"
          />
        ) : (
          <video
            src={item.url + "#t=0.1"}
            controls
            autoPlay
            muted
            playsInline
            preload="auto"
            className="w-full max-h-[85vh] aspect-video object-contain border border-white/10 bg-black"
          />
        )}
      </div>
    </div>
  );
};

export const MediaGallery = ({ id, items = [], accent = "#FF3B30" }) => {
  const [active, setActive] = useState(null);

  if (!items.length) return null;

  return (
    <>
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40">
            Gallery / {String(items.length).padStart(2, "0")} items
          </div>
          <div
            className="font-mono-accent text-[10px] tracking-[0.3em] uppercase"
            style={{ color: accent }}
          >
            Click to expand ↗
          </div>
        </div>

        <div className={`grid grid-cols-2 ${items.length === 3 ? "md:grid-cols-3" : "md:grid-cols-4"} gap-px bg-white/10 border border-white/10`}>
          {items.map((m, i) => (
            <button
              key={i}
              type="button"
              data-testid={`${id}-gallery-item-${i + 1}`}
              onClick={() => setActive(m)}
              className="relative aspect-square bg-[#0a0a0a] overflow-hidden group focus:outline-none"
            >
              {m.type === "image" ? (
                <img
                  src={m.url}
                  alt={m.alt || `${id} media ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover img-hover"
                />
              ) : (
                <>
                  <video
                    src={m.url + "#t=0.5"}
                    muted
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover img-hover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none">
                    <div className="border border-white/40 bg-[#050505]/70 backdrop-blur-sm p-3 group-hover:bg-[#FF3B30] group-hover:border-[#FF3B30] transition-colors">
                      <Play size={20} className="text-white fill-white" />
                    </div>
                  </div>
                </>
              )}
              <div className="absolute top-2 left-2 font-mono-accent text-[9px] tracking-[0.25em] uppercase text-white/80 bg-black/60 px-1.5 py-0.5">
                {String(i + 1).padStart(2, "0")} / {m.type}
              </div>
            </button>
          ))}
        </div>
      </div>

      {active && <MediaLightbox item={active} onClose={() => setActive(null)} />}
    </>
  );
};
